import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import KitModal from '../../../../components/UI/KitModal';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import { FormsService } from '../../../../service/FormService';
import { useNotificationStore } from '../../../../store/storeNotifications';
import { useFormBuilder } from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';

const agentModalSchema = z.object({
  flowId: z.string().min(1, { message: 'Please select a flow' }),
});

type AgentModalFormData = z.infer<typeof agentModalSchema>;

export function AgentModal({
  show,
  onHide,
}: {
  show: boolean;
  onHide: () => void;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const setNotification = useNotificationStore(state => state.setNotification);
  const { state, dispatch, setCurrentPageId } = useFormBuilder();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AgentModalFormData>({
    resolver: zodResolver(agentModalSchema),
    defaultValues: {
      flowId: '',
    },
  });

  async function handleUpdate(data: AgentModalFormData) {
    try {
      setIsLoading(true);

      await FormsService.autoPopulate(state.form.id, {
        flowId: data.flowId,
      }).then(response => {
        dispatch({
          type: ActionTypes.AUTO_POPULATE_FORM,
          payload: {
            newFormNodes: response.data.newFormNodes,
            pages: response.data.pages,
            formSettings: response.data.formSettings,
          },
        }); 

        setCurrentPageId(response.data.pages[0].id);

        setNotification({
          type: 'success',
          message: 'Form auto populated successfully',
        });

      }).catch(() => {
        setNotification({
          type: 'error',
          message: 'Error auto populating form',
        });
      });
    } finally {
      reset();
      onHide();
      
      setIsLoading(false);
    }
  }

  return (
    <KitModal
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '690px',
          background: '#FFF',
        },

        '& .MuiDialogContent-root': {
          background: '#fff',

          p: '24px',
        },
      }}
      fullWidth
      show={show}
      onHide={onHide}
    >
      <form onSubmit={handleSubmit(handleUpdate)}>
        <Box
          mt={'12px'}
          mb={'24px'}
          gap={2}
          display="flex"
          flexDirection="column"
        >
          <Typography variant="h3">Update interface</Typography>

          <Typography variant="body1">
            When updating, your current interface configuration will be lost,
            this may take a few seconds.
          </Typography>

          <Box style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Controller
              name="flowId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  error={!!errors.flowId}
                  helperText={errors.flowId?.message}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      height: '32px',
                      borderRadius: '4px',
                      border: '1px solid #EAECF0',
                      backgroundColor: '#F9FAFB',
                      color: '#475467',
                      margin: '0px',
                    },
                  }}
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value="">
                    <em style={{ color: '#98A2B3' }}>Select a flow</em>
                  </MenuItem>
                  {state.form.flows.map(flow => (
                    <MenuItem key={flow.id} value={flow.id}>
                      {flow.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Typography
              style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#475467',
                fontStyle: 'normal',
              }}
            >
              Choose the agent to use for the auto population of the interface
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={2} width="100%" justifyContent="flex-end">
          <Button onClick={onHide} variant="outlined" type="button">
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={isLoading}>
            {isLoading ? (
              <LoadingSpinner size={16} variant="light" />
            ) : (
              'Update'
            )}
          </Button>
        </Box>
      </form>
    </KitModal>
  );
}
